import { useQuery } from 'react-query';
import api from '../../utils/api';
import { skipWhenOffline } from '../../offline';
import { PatientAttachment, SoapNoteIndexEntry, normalizeList } from './patientFiles';

export const patientFilesQueryKey = (horseId?: string) =>
  ['patient-files', horseId] as const;

const STALE_TIME = 30_000;

/**
 * Loads every attachment for a horse (for the Files tab + its badge count) and,
 * when `withSoapIndex` is set, the horse's SOAP notes so soap-linked files can
 * be labeled with their note. The SOAP-note query is gated behind
 * `withSoapIndex` so the badge-only callers (the chart layouts) don't pay for a
 * fetch they never render.
 *
 * Attachments are online-only (blobs aren't replicated offline), so both
 * queries are disabled offline — the tab shows an offline notice instead.
 */
export function usePatientFiles(
  horseId?: string,
  opts?: {
    withSoapIndex?: boolean;
    /**
     * MAY THIS PERSON READ THE FILES PANEL AT ALL (INV-108 (b))?
     *
     * `GET /attachments` and `GET /soap-notes` both declare `view:medical_records`
     * (attachments.controller.ts:60, soap-notes.controller.ts:55), so a person without it is
     * refused both. The caller passes `patientChartPanels(rbac).files`, which is FALSE only on a
     * KNOWN answer that lacks the slug — so this defaults to TRUE and behaves exactly as it did
     * for every caller that does not pass it, and for every non-answer.
     *
     * It is a parameter rather than a `useRBAC()` call inside the hook because the hook is used
     * for the BADGE COUNT as well as for the tab, and a hook that decided for itself would make
     * the count disagree with the panel on the frame the answer lands.
     */
    canRead?: boolean;
  },
) {
  const withSoapIndex = opts?.withSoapIndex ?? false;
  const canRead = opts?.canRead ?? true;

  const filesQuery = useQuery(
    patientFilesQueryKey(horseId),
    () =>
      api
        .get('/attachments', { params: { horseId } })
        .then((r) => normalizeList<PatientAttachment>(r.data)),
    { enabled: !!horseId && canRead && skipWhenOffline(), staleTime: STALE_TIME },
  );

  const soapQuery = useQuery(
    ['patient-files-soap-index', horseId],
    () =>
      api
        .get('/soap-notes', { params: { horseId } })
        .then((r) => normalizeList<SoapNoteIndexEntry>(r.data)),
    {
      enabled: !!horseId && canRead && withSoapIndex && skipWhenOffline(),
      staleTime: STALE_TIME,
    },
  );

  return {
    attachments: filesQuery.data ?? [],
    soapNotes: soapQuery.data ?? [],
    isLoading: filesQuery.isLoading || (withSoapIndex && soapQuery.isLoading),
    isError: filesQuery.isError,
    refetch: filesQuery.refetch,
  };
}
